import TasksModule from '@modules/TasksModule';
import appManager from '@app/appManager';
import AppModule from '@app/lib/AppModule';
import updateInteractionByChannelId, {
  UpdateInteractionByChannelIdInput,
} from './lib/updateInteractionByChannelId';
import deactivateInteraction from './lib/deactivateInteraction';
import {
  ChannelClassificationInput,
  InteractionContactInput,
  InteractionContactType,
} from '@api/generated/graphql';
import _ from 'lodash';
import { ITask, TaskState } from '@modules/TasksModule/types';
import FeatureFlagModule, { FeatureFlagKeys } from '@modules/FeatureFlagModule';
import { AuthEvents } from '@modules/AuthModule/types';
import { djb2Hash } from '@lib/utils/djb2Hash';

// 'customer' / 'contacts' route the explicit Task.setCustomer / setContacts
// emits through the legacy path, mirroring the FF-on path's coverage.
const legacyFields = new Set([
  'state',
  'attributes',
  'displayName',
  'customer',
  'contacts',
]);

export class InteractionsModule extends AppModule {
  private throttledLegacyUpsert: ((task: ITask) => void) | undefined;
  private readonly pendingUpserts = new Map<string, { dirty: boolean }>();

  override preload(): void {
    this.throttledLegacyUpsert = _.throttle(
      (task: ITask) =>
        this.upsert(task).catch((e) =>
          this.app.logger.error('Error writing interaction', e),
        ),
      3000,
    );

    this.app.events.on(AuthEvents.SIGNOUT, () => {
      this.pendingUpserts.clear();
    });

    this.app.events.on(
      'task.updated',
      ({ task, field }: { task: ITask; field: string }) => {
        if (
          FeatureFlagModule.isEnabled(
            FeatureFlagKeys.featureReduceInteractionUpdateRequests,
          )
        ) {
          if (
            field === 'customer' ||
            field === 'contacts' ||
            field === 'state' ||
            field === 'id'
          ) {
            this.evaluateAndRecord(task).catch((e) =>
              this.app.logger.error('Error writing interaction', e),
            );
          }
        } else if (this.isRecordableState(task) && legacyFields.has(field)) {
          if (field === 'state' && task.state === TaskState.Completed) {
            this.upsert(task).catch((e) =>
              this.app.logger.error('Error writing interaction', e),
            );
          } else {
            this.throttledLegacyUpsert?.(task);
          }
        }
      },
    );
  }

  public async writeClassificationInfo(
    task: ITask,
    info: ChannelClassificationInput,
  ) {
    return this.upsert(task, { channelClassification: info });
  }

  public async deactivateInteraction(channelId: string): Promise<boolean> {
    return await deactivateInteraction(channelId);
  }

  /**
   * Direct contact write to an interaction, mirroring `writeClassificationInfo`.
   * Used as a fallback when the `task.updated` listener chain can't fire
   * (e.g. post-transfer when TasksModule has already disposed the task and
   * the caller holds a snapshot of the Task object).
   *
   * Verifies `task.id === task.attributes.sessionSid` so we never send the
   * kazoo call id to the BFF — until PCS correlation completes, `task.id`
   * is still the kazoo id, which the BFF cannot resolve.
   */
  public async writeContactsInfo(
    task: ITask,
    contacts: InteractionContactInput[],
  ) {
    if (!this.hasValidSessionSid(task)) {
      this.app.logger.warn(
        'writeContactsInfo: task not correlated to PCS — skipping write',
        { taskId: task.id, sessionSid: task.attributes.sessionSid },
      );
      return;
    }
    return this.upsert(task, { contacts });
  }

  /**
   * True when the task's id is the PCS session sid (channel id) — i.e. PCS
   * correlation has completed. Until then, `task.id` is the kazoo call id,
   * which the BFF cannot resolve.
   */
  private hasValidSessionSid(task: ITask): boolean {
    return (
      !!task.attributes.sessionSid && task.id === task.attributes.sessionSid
    );
  }

  private isRecordableState(task: ITask): boolean {
    if (!this.hasValidSessionSid(task)) {
      return false;
    }

    if (
      task.state !== TaskState.Accepted &&
      task.state !== TaskState.WrapUp &&
      task.state !== TaskState.Completed
    ) {
      return false;
    }

    if (task.attributes.recordInteraction === true) {
      return true;
    }

    // When the technician-conversation-history FF is on, allow upserts for
    // tasks that started with recordInteraction=false ONLY when a Technician
    // contact has been attached. Other recordInteraction=false signals (no
    // tenant access, classification handled elsewhere, completed-by-others)
    // must continue to suppress the upsert.
    if (
      FeatureFlagModule.isEnabled(
        FeatureFlagKeys.featureDisplayTechnicianConversationHistory,
      )
    ) {
      const contacts = task.attributes.contacts as
        | InteractionContactInput[]
        | undefined;
      const hasTechnicianContact = !!contacts?.some(
        (c) => c.type === InteractionContactType.Technician,
      );
      if (hasTechnicianContact) {
        return true;
      }
    }

    return false;
  }

  private buildSentHash(
    customerId: string | undefined,
    customerName: string | undefined,
    contacts: InteractionContactInput[] | undefined,
  ): string {
    // Contacts are sorted by id so array order doesn't affect the hash.
    // A library like object-hash could handle deeper normalization automatically,
    // but manual sort + djb2 over JSON.stringify is sufficient for this flat structure.
    const normalizedContacts = contacts
      ? [...contacts].sort((a, b) => a.id.localeCompare(b.id))
      : null;
    return djb2Hash(
      JSON.stringify([
        customerId ?? null,
        customerName ?? null,
        normalizedContacts,
      ]),
    );
  }

  private hasSamePayloadAsLastSent(task: ITask): {
    same: boolean;
    hash: string;
  } {
    const customerName =
      (task.attributes.customerId && task.displayName) || undefined;
    const hash = this.buildSentHash(
      task.attributes.customerId,
      customerName,
      task.attributes.contacts,
    );
    const lastHash = task.attributes.lastSentHash as string | undefined;
    return { same: !!lastHash && lastHash === hash, hash };
  }

  private async evaluateAndRecord(task: ITask): Promise<void> {
    if (!this.isRecordableState(task)) return;

    // In-flight check must run BEFORE hasSamePayloadAsLastSent. While an
    // upsert is pending, lastSentHash still reflects the previous sent
    // value — a change that briefly matches the *previous* lastSent (e.g.,
    // back-button clear after select) would otherwise short-circuit here and
    // leave the dirty flag unset, losing the change.
    const inflight = this.pendingUpserts.get(task.id);
    if (inflight) {
      inflight.dirty = true;
      return;
    }

    const { same, hash } = this.hasSamePayloadAsLastSent(task);
    if (same) return;

    const entry = { dirty: false };
    this.pendingUpserts.set(task.id, entry);
    try {
      const interaction = await this.upsert(task);
      // Guard against the task being deleted (e.g., setTaskComplete) while
      // upsert was in flight — setTaskAttribute throws if the task is gone,
      // which would surface as a misleading "Error writing interaction" log.
      if (interaction && TasksModule.getTask(task.id)) {
        TasksModule.setTaskAttribute(task.id, 'lastSentHash', hash);
      }
    } finally {
      this.pendingUpserts.delete(task.id);
    }

    // Guard the trailing-edge retry against sign-out: handleClearTasks may
    // have wiped the task while we were in flight, in which case the local
    // entry.dirty closure could still trigger a stale post-logout upsert.
    if (entry.dirty && TasksModule.getTask(task.id)) {
      return this.evaluateAndRecord(task);
    }
  }

  private async upsert(
    task: ITask,
    extra: Partial<UpdateInteractionByChannelIdInput> = {},
  ) {
    // Coerce empty string to undefined so the BFF doesn't receive a blank tenantId.
    const tenantId = task.tenantId || undefined;

    if (
      !tenantId &&
      !FeatureFlagModule.isEnabled(
        FeatureFlagKeys.featureReduceInteractionUpdateRequests,
      )
    ) {
      return;
    }

    this.app.logger.info('Interaction sync', task.id, task.state);

    const payload: UpdateInteractionByChannelIdInput = {
      customerId: task.attributes.customerId ?? undefined,
      customerName:
        (task.attributes.customerId && task.displayName) || undefined,
      contacts: task.attributes.contacts as
        | InteractionContactInput[]
        | undefined,
      ...extra,
    };

    const interaction = await updateInteractionByChannelId(
      task.id,
      tenantId,
      payload,
    );

    if (
      interaction &&
      !task.attributes.interactionId &&
      TasksModule.getTask(task.id)
    ) {
      TasksModule.setTaskAttribute(task.id, 'interactionId', interaction.id);
      TasksModule.setTaskAttribute(
        task.id,
        'displayId',
        interaction?.displayId,
      );
    }

    return interaction;
  }
}

export default new InteractionsModule(
  appManager.events,
  appManager.logger('Interactions'),
);
